import type { MutableRefObject } from "react";

import dayjs from "dayjs";
import get from "lodash/get";

import { LegendWithCheckboxAndFavicon } from "@similarweb/ui-components";

import { toTimeGranularityName, type TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { RawDate } from "app/UtilitiesAndConstants/types/dates";
import {
    addPartialDataZones,
    isPartialDataPoint,
} from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import type { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import type ColorStack from "app/components/colorsStack/ColorStack";
import { SEGMENT_ID } from "app/components/dashboard/widget-wizard/components/segments/constants";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { RegionalsLegendsWithFaviconAndCheckbox } from "app/components/React/Legends/RegionalsLegendsWithFaviconAndCheckbox";
import { SegmentLegendsWithFaviconAndCheckbox } from "app/components/React/Legends/SegmentLegendsWithFaviconAndCheckbox";
import { translate } from "app/filters/i18nFilter";
import { resolveWebSourceForGraphApi } from "app/pages/industry-analysis/category-share/CategoryShareGraph/utils/CategoryShareGraphUtils";
import type { WebSourceType } from "app/pages/industry-analysis/category-share/CategoryShareTypes";
import { COMMON_COLORS } from "app/pages/industry-analysis/common/commonColors";
import { RegionalsLegendItemHint } from "app/pages/industry-analysis/players/market-shares/RegionalsLegendItemHint";
import {
    CHART_DISPLAY_TYPE_MD,
    getDomainSubtitle,
} from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { isLinesOfBusinessSegment, parseLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { getAverage } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";

import type {
    IChart,
    IChartData,
    IRawData,
    EnrichedData,
    SwitchGraphDomainData,
    IDomainDataV2,
    RegionalDomainData,
    SwitchGraphTotalMetrics,
} from "../types";
import { EApiKeys } from "../types";

import { DEFAULT_UN_CHECK_COLOR, getOthersDomainName, INITIAL_SELECTION } from "./const";

const roundToPrecision = (value: number): number => {
    return Math.round(value * 10000) / 10000;
};

export const getChartDataWithOthers = (visibleChartData: IChart[], allRawData: IChart[]): IChart[] => {
    const othersTemplate = allRawData.find(({ name }) => name === getOthersDomainName());
    if (!othersTemplate) {
        console.warn("Others template not found in raw data");
        return visibleChartData;
    }

    const chartDataWithoutOthers = visibleChartData.filter(({ name }) => name !== getOthersDomainName());

    if (chartDataWithoutOthers.length === 0) {
        return visibleChartData;
    }

    const othersData = structuredClone(othersTemplate);

    let totalOthersAverage = 1;

    chartDataWithoutOthers.forEach((domainChart) => {
        if (domainChart.average != null && typeof domainChart.average === "number") {
            const preciseAverage = roundToPrecision(domainChart.average);
            totalOthersAverage -= preciseAverage;
        }
    });

    othersData.data.forEach((othersDataPoint, timeIndex) => {
        // Start with 100% market share (Others = 1 - sum of visible domains)
        let othersShare = 1;

        chartDataWithoutOthers.forEach((domainChart) => {
            const domainDataPoint = domainChart.data[timeIndex];
            if (domainDataPoint?.y != null && typeof domainDataPoint.y === "number") {
                // Apply precision rounding to avoid floating point errors
                const preciseValue = roundToPrecision(domainDataPoint.y);
                othersShare -= preciseValue;
            }
        });

        othersDataPoint.y = Math.max(0, roundToPrecision(othersShare));
    });

    const othersWithStyling = {
        ...othersData,
        color: COMMON_COLORS.OTHER_CHART,
        zoneAxis: null,
        average: Math.max(0, roundToPrecision(totalOthersAverage)),
    };

    return [...chartDataWithoutOthers, othersWithStyling];
};

const initOthers = (chartData: IChart[]): IChart[] => {
    const newChartData = structuredClone(chartData);
    const othersData = Array(chartData[0].data.length);
    const indexWithMostDataPoints = chartData.reduce((maxIndex, dataItem, index) => {
        if (dataItem.data.length > maxIndex) {
            return index;
        }
        return maxIndex;
    }, 0);
    // init others data with dates:
    chartData[indexWithMostDataPoints].data.forEach((singleChartData, singleChartDataIndex) => {
        othersData[singleChartDataIndex] = {
            x: singleChartData.x,
            y: 1,
        };
    });

    const others = {
        name: getOthersDomainName(),
        color: COMMON_COLORS.OTHER_CHART,
        data: othersData,
        id: getOthersDomainName(),
        average: 0,
        index: 0,
    };

    newChartData.push(others);
    return newChartData;
};

const getParsedData = (
    avgGetter: (item?: SwitchGraphTotalMetrics) => number | null,
    graphData: SwitchGraphDomainData[],
    selectedDataToparse: EApiKeys,
    keys?: Record<string, any>,
    webSource?: WebSourceType,
): IChart[] => {
    const adaptedWebSource = resolveWebSourceForGraphApi(webSource);

    const getDataPoints = (graphDataItem: IDomainDataV2[]) => {
        return graphDataItem.map(
            (item: IDomainDataV2): IChartData => ({
                x: dayjs.utc(item.Key).valueOf(),
                y: item.Value[selectedDataToparse],
            }),
        );
    };

    const flattenData = graphData.map((switchGraphDomainData, index) => {
        const sourceData = switchGraphDomainData.Metrics.find((metric) => metric.WebSource === adaptedWebSource);
        const data = getDataPoints(sourceData?.AvgMonthVisits ?? []);
        const averageNum = avgGetter(sourceData?.TotalMetrics) ?? getAverage(data);
        const currentItem = keys && keys.find((k) => k.id === switchGraphDomainData.Id);

        const segmentName = get(currentItem, "segmentName", "");
        const subtitleText = get(currentItem, "subtitleText", "");
        const domain = get(currentItem, "domain", "");
        const name = get(currentItem, "name", "");
        const isSegment = get(currentItem, "isSegment", "");

        const segmentId =
            currentItem && currentItem.domain !== switchGraphDomainData.Id ? switchGraphDomainData.Id : undefined;

        return {
            name: segmentName || subtitleText || switchGraphDomainData.Id,
            color: undefined,
            data,
            segmentId,
            isSegment: currentItem?.type === SEGMENT_ID || isSegment,
            id: switchGraphDomainData.Id,
            domain: name || domain,
            average: averageNum,
            index: Object.keys(graphData).length - index,
        };
    });

    return initOthers(flattenData);
};

export const parseRawData = (
    graphData: SwitchGraphDomainData[],
    keys?: Record<string, any>,
    webSource?: WebSourceType,
): IRawData => {
    if (!graphData) {
        return null;
    }
    if (!webSource) {
        webSource = proNavigator.getApiParams().webSource;
    }

    const getTotalAvgMonthVisits = (domainData: SwitchGraphDomainData): number => {
        const webSourceData = domainData.Metrics.find(
            (metric) => metric.WebSource === resolveWebSourceForGraphApi(webSource),
        );
        return webSourceData?.TotalMetrics?.AvgMonthVisits ?? 0;
    };

    const sortedGraphData = graphData.sort((a, b) => getTotalAvgMonthVisits(b) - getTotalAvgMonthVisits(a));

    return {
        Absolute: getParsedData((item) => item?.AvgMonthVisits, sortedGraphData, EApiKeys.ABSOLUTE, keys, webSource),
        Share: getParsedData((item) => item?.AvgMonthVisitsShare, sortedGraphData, EApiKeys.SHARE, keys, webSource),
    };
};

const getColorFromLegend = (legendItems: ILegendItems, itemName: string): string => {
    const foundItem = legendItems.find((legendItem) => legendItem.name === itemName);
    return foundItem ? foundItem.color : DEFAULT_UN_CHECK_COLOR;
};

export const parseLegendsRawData = (
    rawData: IChart[],
    enrichedData: EnrichedData,
    chartDisplayType: EChartDisplayType,
    colorStack: MutableRefObject<ColorStack>,
    previousLegendItems: ILegendItems, // for keeping the selected colors when switching tabs
    getUserAndAccountSegmentById: (segmentId: string) => ICustomSegment,
    onViewCountryDomainsClick?: (parentDomainName: string, domains: RegionalDomainData[]) => void,
): ILegendItems => {
    const parsedRawData = rawData.map((dataItem) => {
        const { id, data } = dataItem;
        const enrichedDataItem = enrichedData[id];
        const regionalDomains = enrichedDataItem?.regionalDomains;
        const hasRegionalDomains = regionalDomains && regionalDomains.length > 1;
        const { formatter, averageValueMetric } = CHART_DISPLAY_TYPE_MD[chartDisplayType];
        const averageNum = enrichedDataItem ? enrichedDataItem[averageValueMetric] : getAverage(data);
        const displayData = formatter(averageNum);
        const segment = getUserAndAccountSegmentById(id);

        let isLobSegment = false;
        let lobDomainName = null;
        let lobSegmentName = null;
        if (!segment && isLinesOfBusinessSegment(id)) {
            isLobSegment = true;

            if (enrichedDataItem?.name) {
                lobSegmentName = enrichedDataItem.name;
                lobDomainName = enrichedDataItem.domain;
            } else {
                // Fallback to parsing the segment ID
                const lobSegmentData = parseLinesOfBusinessSegmentId(id);
                if (lobSegmentData) {
                    lobDomainName = lobSegmentData.domain;
                    lobSegmentName = lobSegmentData.businessLineName;
                }
            }
        }

        const isSegmentLike = !!(segment || isLobSegment);
        const isOther = dataItem.id === getOthersDomainName();

        let customLegendComponent;
        if (isSegmentLike) {
            customLegendComponent = SegmentLegendsWithFaviconAndCheckbox;
        } else if (hasRegionalDomains) {
            customLegendComponent = RegionalsLegendsWithFaviconAndCheckbox;
        } else {
            customLegendComponent = LegendWithCheckboxAndFavicon;
        }

        let tooltip;
        if (isSegmentLike) {
            if (isLobSegment) {
                tooltip = translate("market.players.market.shares.chart.legend.tooltip.lob");
            } else {
                tooltip = translate("market.players.market.shares.chart.legend.tooltip.segment");
            }
        } else if (!isOther && hasRegionalDomains) {
            tooltip = RegionalsLegendItemHint({
                parentDomainName: enrichedDataItem?.id,
                domains: regionalDomains,
                onViewCountryDomainsClick,
            });
        } else {
            tooltip = undefined;
        }

        let subtitleText: string | null = null;
        if (isSegmentLike) {
            if (segment && segment.segmentName) {
                subtitleText = segment.segmentName;
            } else if (lobSegmentName) {
                subtitleText = lobSegmentName;
            } else {
                subtitleText = null;
            }
        } else if (hasRegionalDomains) {
            const domainCount = regionalDomains ? regionalDomains.length : 0;
            subtitleText = getDomainSubtitle(segment, domainCount);
        }

        const previousItem = previousLegendItems.find((item) => item.id === dataItem.id);
        const previousColor =
            previousItem?.color ||
            (previousLegendItems.length ? getColorFromLegend(previousLegendItems, dataItem.name) : undefined);

        const isVisible = previousItem
            ? previousItem.visible
            : (!!previousColor && previousColor !== DEFAULT_UN_CHECK_COLOR) || dataItem.name === getOthersDomainName();

        let displayName;
        if (segment) {
            displayName = segment.domain;
        } else if (isLobSegment && lobDomainName) {
            displayName = lobDomainName;
        } else {
            displayName = dataItem.name;
        }

        return {
            name: displayName,
            isSegment: !!segment || isLobSegment,
            id: dataItem.id,
            visible: isVisible,
            color: previousColor ? previousColor : DEFAULT_UN_CHECK_COLOR,
            data: displayData,
            subtitleText,
            favicon: enrichedDataItem?.favicon,
            customLegendComponent: isOther ? null : customLegendComponent,
            tooltip,
            ...(hasRegionalDomains
                ? {
                      tooltipProps: { closeDelay: 50, allowHover: true },
                  }
                : {}),
        };
    });

    if (!previousLegendItems.length) {
        for (let i = 0; i < INITIAL_SELECTION && i < parsedRawData.length; i++) {
            parsedRawData[i].visible = true;
            parsedRawData[i].color = colorStack.current.acquire();
        }
        // If all items are checked, uncheck 'Others'
        const allChecked = parsedRawData.every((item) => item.visible);
        const othersItem = parsedRawData.find((item) => item.name === getOthersDomainName());
        if (allChecked && othersItem) {
            othersItem.visible = false;
            othersItem.color = DEFAULT_UN_CHECK_COLOR;
        }
    }

    return parsedRawData;
};

export const getChartData = (
    legendItems: ILegendItems,
    rawData: IChart[],
    showOthers: boolean,
    timeGranularity: TimeGranularitySymbol,
    isMonthToDate: boolean,
    lastSupportedDate: RawDate,
): IChart[] => {
    const newRawData = [...rawData];
    const chartData = legendItems
        .filter((legendItem) => !!legendItem.visible)
        .map((legendItem) => {
            const visibleChartDataItem = newRawData.find((dataItem) => dataItem.id === legendItem.id);
            if (visibleChartDataItem) {
                visibleChartDataItem.color = legendItem.color;
                visibleChartDataItem.data = visibleChartDataItem.data.map((dataPoint, idx) => {
                    const isPartial = isMonthToDate
                        ? isPartialDataPoint(
                              idx,
                              visibleChartDataItem.data,
                              { Key: dataPoint.x },
                              toTimeGranularityName(timeGranularity) as GranularityNames,
                              lastSupportedDate,
                          )
                        : false;

                    return {
                        ...dataPoint,
                        isPartial,
                    };
                });
                return visibleChartDataItem;
            }

            return {
                color: legendItem.color,
                name: legendItem.name,
                subtitleText: legendItem.subtitleText,
                data: [],
            };
        })
        .filter(Boolean);

    const filteredChartData = showOthers ? chartData : chartData.filter(({ name }) => name !== getOthersDomainName());

    const chartDataWithPartial = addPartialDataZones(filteredChartData, {
        chartType: chartTypes.AREA,
    });

    return showOthers ? getChartDataWithOthers(chartDataWithPartial, newRawData) : chartDataWithPartial;
};
